import type { FC, ReactNode } from 'react'
import type { Wallpaper } from '@/types'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Check, Copy, Download, ExternalLink } from 'lucide-react'
import { Button, buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { getColorFamily, getColorFamilyInfo, toCssColor } from '@/lib/colors'
import { formatNumber, getDeviceLabel } from '@/lib/format'

interface WallpaperDetailsProps {
    wallpaper: Wallpaper
}

const getFileType = (url: string): string | null => {
    const match = url.split('?')[0].match(/\.([a-z0-9]+)$/i)

    if (!match) return null

    const extension = match[1].toLowerCase()

    return extension === 'jpeg' ? 'JPG' : extension.toUpperCase()
}

export const WallpaperDetails: FC<WallpaperDetailsProps> = ({ wallpaper }): ReactNode => {
    const [downloads, setDownloads] = useState(wallpaper.downloads)
    const [downloadStarted, setDownloadStarted] = useState(false)
    const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
    const timers = useRef<Array<ReturnType<typeof setTimeout>>>([])

    useEffect(() => {
        setDownloads(wallpaper.downloads)
        setDownloadStarted(false)
        setCopyState('idle')
    }, [wallpaper.id, wallpaper.downloads])

    useEffect(() => {
        const pending = timers.current

        return () => pending.forEach(clearTimeout)
    }, [])

    const later = (callback: () => void, delay: number) => {
        timers.current.push(setTimeout(callback, delay))
    }

    const onDownload = () => {
        setDownloads(count => count + 1)
        setDownloadStarted(true)
        later(() => setDownloadStarted(false), 4000)
    }

    const onCopy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href)
            setCopyState('copied')
        }
        catch {
            setCopyState('failed')
        }

        later(() => setCopyState('idle'), 2500)
    }

    const hasSize = Boolean(wallpaper.width && wallpaper.height)
    const family = getColorFamilyInfo(getColorFamily(wallpaper.color))
    const fileType = getFileType(wallpaper.image)
    const hasCategory = Boolean(wallpaper.category_id && wallpaper.category_name && wallpaper.category_slug)

    return (
        <div className='space-y-8'>
            <div>
                {hasCategory && (
                    <Link
                        href={`/categories/${wallpaper.category_slug}`}
                        className='inline-flex h-7 items-center rounded-full border border-white/10 px-3 text-xs font-medium text-foreground/80 transition hover:bg-white/[0.06] hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                    >
                        <span data-mdlfy-cms={`categories:${wallpaper.category_id}:name`}>
                            {wallpaper.category_name}
                        </span>
                    </Link>
                )}

                <h1
                    className='mt-4 font-heading text-4xl leading-[1.05] tracking-tight sm:text-5xl'
                    data-mdlfy-cms={`wallpapers:${wallpaper.id}:title`}
                >
                    {wallpaper.title}
                </h1>

                {wallpaper.description && (
                    <p
                        className='mt-4 text-[0.95rem] leading-relaxed text-muted-foreground'
                        data-mdlfy-cms={`wallpapers:${wallpaper.id}:description`}
                    >
                        {wallpaper.description}
                    </p>
                )}

                {wallpaper.artist && (
                    <p className='mt-3 text-sm text-muted-foreground'>
                        {'By '}
                        {wallpaper.artist_url ? (
                            <a
                                href={wallpaper.artist_url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='font-medium text-foreground underline-offset-4 hover:underline'
                            >
                                <span data-mdlfy-cms={`wallpapers:${wallpaper.id}:artist`}>
                                    {wallpaper.artist}
                                </span>
                            </a>
                        ) : (
                            <span className='font-medium text-foreground' data-mdlfy-cms={`wallpapers:${wallpaper.id}:artist`}>
                                {wallpaper.artist}
                            </span>
                        )}
                    </p>
                )}
            </div>

            <div className='space-y-3'>
                <a
                    href={`/api/download/${wallpaper.slug}`}
                    rel='nofollow'
                    onClick={onDownload}
                    className={cn(buttonVariants({ size: 'xl' }), 'w-full')}
                >
                    <Download aria-hidden='true' />
                    Download wallpaper
                </a>

                <div className='grid grid-cols-2 gap-3'>
                    <Button variant='outline' size='lg' onClick={onCopy} className='w-full'>
                        {copyState === 'copied' ? <Check aria-hidden='true' /> : <Copy aria-hidden='true' />}
                        {copyState === 'copied' ? 'Link copied' : copyState === 'failed' ? 'Copy failed' : 'Copy link'}
                    </Button>

                    <a
                        href={wallpaper.image}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'w-full')}
                    >
                        <ExternalLink aria-hidden='true' />
                        Full size
                    </a>
                </div>

                <p aria-live='polite' className='min-h-5 text-center text-xs text-muted-foreground'>
                    {downloadStarted ? 'Your download is starting.' : (
                        <>
                            {fileType && <span>{fileType}</span>}
                            {fileType && hasSize && ' · '}
                            {hasSize && (
                                <span className='font-mono'>
                                    <span>{wallpaper.width}</span>
                                    {' × '}
                                    <span>{wallpaper.height}</span>
                                </span>
                            )}
                            {(fileType || hasSize) && ' · '}
                            Free to download
                        </>
                    )}
                </p>
            </div>

            <dl className='grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-white/[0.07] ring-1 ring-white/[0.07]'>
                <div className='bg-background p-4'>
                    <dt className='text-xs font-medium text-muted-foreground'>
                        Resolution
                    </dt>
                    <dd className='mt-1 font-mono text-sm text-foreground'>
                        {hasSize ? (
                            <>
                                <span>{wallpaper.width}</span>
                                {' × '}
                                <span>{wallpaper.height}</span>
                            </>
                        ) : 'Original size'}
                    </dd>
                </div>

                <div className='bg-background p-4'>
                    <dt className='text-xs font-medium text-muted-foreground'>
                        Made for
                    </dt>
                    <dd className='mt-1 text-sm text-foreground'>
                        {getDeviceLabel(wallpaper.portrait)}
                    </dd>
                </div>

                <div className='bg-background p-4'>
                    <dt className='text-xs font-medium text-muted-foreground'>
                        Downloads
                    </dt>
                    <dd className='mt-1 text-sm text-foreground'>
                        {formatNumber(downloads)}
                    </dd>
                </div>

                <div className='bg-background p-4'>
                    <dt className='text-xs font-medium text-muted-foreground'>
                        Color
                    </dt>
                    <dd className='mt-1 flex items-center gap-2 text-sm text-foreground'>
                        {wallpaper.color ? (
                            <>
                                <span aria-hidden='true' className='size-4 shrink-0 rounded-full ring-1 ring-white/20' style={{ backgroundColor: toCssColor(wallpaper.color) }} />
                                {family ? (
                                    <Link
                                        href={`/wallpapers?color=${family.slug}`}
                                        className='rounded underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                    >
                                        {family.label}
                                    </Link>
                                ) : (
                                    <span className='font-mono'>{wallpaper.color}</span>
                                )}
                            </>
                        ) : 'Mixed'}
                    </dd>
                </div>
            </dl>

            {wallpaper.tags.length > 0 && (
                <div>
                    <h2 className='text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase'>
                        Tags
                    </h2>

                    <ul className='mt-3 flex flex-wrap gap-2'>
                        {wallpaper.tags.map(tag => (
                            <li key={tag}>
                                <Link
                                    href={`/wallpapers?q=${encodeURIComponent(tag)}`}
                                    className='inline-flex h-8 items-center rounded-full bg-white/[0.06] px-3 text-sm text-foreground/85 transition hover:bg-white/[0.12] hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none'
                                >
                                    {tag}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className='space-y-2'>
                <h2 className='text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase'>
                    Set it as your wallpaper
                </h2>

                <details open={!wallpaper.portrait} className='group rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 [&_summary::-webkit-details-marker]:hidden'>
                    <summary className='flex cursor-pointer list-none items-center justify-between text-sm font-medium text-foreground'>
                        On a computer
                        <span aria-hidden='true' className='text-muted-foreground transition group-open:rotate-45'>+</span>
                    </summary>

                    <ul className='mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground'>
                        <li>On Windows, right-click the downloaded file and choose Set as desktop background.</li>
                        <li>On a Mac, Control-click the file in Finder and choose Set Desktop Picture.</li>
                    </ul>
                </details>

                <details open={wallpaper.portrait} className='group rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-3 [&_summary::-webkit-details-marker]:hidden'>
                    <summary className='flex cursor-pointer list-none items-center justify-between text-sm font-medium text-foreground'>
                        On a phone
                        <span aria-hidden='true' className='text-muted-foreground transition group-open:rotate-45'>+</span>
                    </summary>

                    <ul className='mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground'>
                        <li>On iPhone, save the image to Photos, open it, tap Share and choose Use as Wallpaper.</li>
                        <li>On Android, open the image in your gallery app, tap the menu and choose Set as wallpaper.</li>
                    </ul>
                </details>
            </div>
        </div>
    )
}
